import React, { useState } from 'react';
import { cardSchemeLogoSrc, CARD_SCHEME_NAMES } from './cardSchemeLogos';

// What a card checkout must show the customer (card processor go-live checklist): the card
// schemes accepted, the venue's legal entity, address and contact details, and its terms,
// refund and privacy policies. `legal` is the checkout_legal object the API sends (built by
// aws_handlers/shared/checkout-legal.js); blank fields are simply not shown. The same file is
// copied into the booking widget, pay-app and online-ordering-app — keep the copies identical.

const DEFAULT_SCHEMES = ['visa', 'mastercard'];

export function CardSchemeLogos({ schemes }) {
  const list = (Array.isArray(schemes) && schemes.length ? schemes : DEFAULT_SCHEMES)
    .filter((s) => cardSchemeLogoSrc(s));
  return (
    <div className="checkout-legal-logos" aria-label="Cards accepted" style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
      {list.map((s) => (
        <img key={s} src={cardSchemeLogoSrc(s)} alt={CARD_SCHEME_NAMES[s]} title={CARD_SCHEME_NAMES[s]}
          width={38} height={24} style={{ display: 'block', width: 38, height: 24 }} />
      ))}
    </div>
  );
}

const linkStyle = {
  background: 'none', border: 'none', padding: 0, font: 'inherit', color: 'inherit',
  textDecoration: 'underline', cursor: 'pointer',
};

function PolicyModal({ title, html, onClose }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 16, zIndex: 2147483000,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#fff', color: '#334155', borderRadius: 12, width: '100%', maxWidth: 560,
          maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden', textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e5e7eb' }}>
          <h3 style={{ margin: 0, fontSize: 17, color: '#0f172a' }}>{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{ background: 'none', border: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: '#555' }}>
            ×
          </button>
        </div>
        {/* Venue-written HTML from General Settings, rendered like the venue's T&Cs. */}
        <div style={{ padding: 20, overflowY: 'auto', fontSize: 14, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    </div>
  );
}

export default function CheckoutLegal({ legal, showLogos = true }) {
  const [open, setOpen] = useState(null);
  const l = legal && typeof legal === 'object' ? legal : {};

  const policies = [
    { title: 'Terms & Conditions', html: l.terms_and_conditions },
    { title: 'Refund Policy', url: l.refund_policy_url, html: l.refund_policy },
    { title: 'Privacy Policy', url: l.privacy_policy_url, html: l.privacy_policy },
  ].filter((p) => p.url || p.html);

  const identity = [
    l.legal_name,
    l.company_number ? `Company no. ${l.company_number}` : null,
    l.address,
  ].filter(Boolean);

  return (
    <div className="checkout-legal" style={{ marginTop: 16, fontSize: 12, lineHeight: 1.5, color: '#64748b', textAlign: 'left' }}>
      {showLogos && <CardSchemeLogos schemes={l.card_schemes} />}
      {identity.length > 0 && <div style={{ marginTop: 8 }}>{identity.join(' · ')}</div>}
      {(l.email || l.phone) && (
        <div>
          {l.email && <a href={`mailto:${l.email}`} style={{ color: 'inherit' }}>{l.email}</a>}
          {l.email && l.phone && ' · '}
          {l.phone && <a href={`tel:${l.phone.replace(/[^\d+]/g, '')}`} style={{ color: 'inherit' }}>{l.phone}</a>}
        </div>
      )}
      {policies.length > 0 && (
        <div style={{ marginTop: 4 }}>
          {policies.map((p, i) => (
            <React.Fragment key={p.title}>
              {i > 0 && ' · '}
              {p.url ? (
                <a href={p.url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>{p.title}</a>
              ) : (
                <button type="button" style={linkStyle} onClick={() => setOpen(p)}>{p.title}</button>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
      {open && <PolicyModal title={open.title} html={open.html} onClose={() => setOpen(null)} />}
    </div>
  );
}
